import get from "lodash/get";

import categoryService from "app/common/services/categoryService";
import { swSettings } from "app/common/services/swSettings";
import { isTimeGranularityName } from "app/common/utils/timeGranularity";
import type { TimeGranularityName } from "app/common/utils/timeGranularity";
import ColorStack from "app/components/colorsStack/ColorStack";
import {
    MARKET_SHARES_GRAPH_METRIC_TYPE,
    MARKET_SHARES_METRIC_TYPE,
} from "app/components/dashboard/widget-wizard/components/MarketAnalysis/constants";
import type { Domain } from "app/components/dashboard/widget-wizard/components/MarketAnalysis/types";
import { CHART_COLORS } from "app/constants/ChartColors";
import { simplePercentageFilter } from "app/filters/ngFilters";
import { numberFilter } from "app/filters/numberFilter";
import { COMMON_COLORS } from "app/pages/industry-analysis/common/commonColors";
import type {
    EnrichedData,
    IChart,
    IEnrichedDataMetrics,
    Records,
    SwitchGraphDomainData,
} from "app/pages/industry-analysis/players/market-shares/types";
import { extractPrimaryCategoryFromParams } from "app/pages/industry-analysis/routes/routeUtils";
import { OTHERS_DOMAIN_NAME } from "app/pages/keyword-analysis/TotalTrafficPage/Graph/GraphData";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";

import type { ApiParams, ColumnChartData } from "./types";

type MarketSharesMetricType = typeof MARKET_SHARES_METRIC_TYPE | typeof MARKET_SHARES_GRAPH_METRIC_TYPE;

/**
 * Resolves API time granularity for Market Shares widgets:
 * - Bar chart: always monthly
 * - Line chart: honors saved filter
 */
export const resolveMarketSharesApiTimeGranularity = (
    filters: { timeGranularity?: string },
    isWindow: boolean | undefined,
    widgetMetricType: MarketSharesMetricType,
): TimeGranularityName => {
    if (widgetMetricType === MARKET_SHARES_METRIC_TYPE) {
        return "Monthly";
    }

    if (isTimeGranularityName(filters?.timeGranularity)) {
        return filters.timeGranularity;
    }

    return isWindow ? "Daily" : "Monthly";
};

export const getColumnChartData = (
    enrichedData: EnrichedData,
    averageValueMetric: keyof IEnrichedDataMetrics,
): ColumnChartData[] => {
    const itemsKeys = Object.keys(enrichedData);

    const colors = new ColorStack(CHART_COLORS.chartMainColors);

    const columnChartData = itemsKeys.map((id) => {
        const enrichedDataItem = enrichedData[id];

        const y = enrichedDataItem[averageValueMetric];

        const sharedAttributes = {
            id,
            y,
            displayText: id,
            favicon: enrichedDataItem?.favicon,
            color: colors.acquire(),
            name: enrichedDataItem.name,
            domain: enrichedDataItem.domain,
            segmentId: enrichedDataItem.segmentId,
            isSegment: enrichedDataItem.isSegment ?? false,
        };

        return sharedAttributes;
    });

    return columnChartData;
};

export const getIsOtherEmpty = (data: IChart[]) => {
    const othersValue = data?.find((item) => item.id === OTHERS_DOMAIN_NAME);

    const allItemsCount = othersValue?.data?.reduce((acc, cur) => (acc += cur.y), 0);

    const persentValue = simplePercentageFilter()(allItemsCount, 2);

    return !persentValue;
};

export const getDataWithOthers = (data: ColumnChartData[]) => {
    const allItemsCount = data.reduce((acc, cur) => acc + Math.round(cur.y * 10000) / 10000.0, 0);

    const result = data.map((item) => ({
        ...item,
        y: Math.round(item.y * 10000) / 100.0,
    }));
    const percentValue = Math.round((1 - allItemsCount) * 10000) / 100.0;

    let other: number;

    if (percentValue && percentValue < 1) {
        const otherCalculation = result.reduce((acc, cur) => acc + Math.round(cur.y * 10000) / 10000.0, 0);

        other = Math.round((1 - otherCalculation) * 10000) / 100.0;
    } else {
        other = Number(numberFilter()(percentValue, 2));
    }

    if (other > 0) {
        result.push({
            y: other,
            id: OTHERS_DOMAIN_NAME,
            name: OTHERS_DOMAIN_NAME,
            displayText: OTHERS_DOMAIN_NAME,
            domain: OTHERS_DOMAIN_NAME,
            favicon: "",
            color: COMMON_COLORS.OTHER_CHART,
            isSegment: false,
        });
    }

    return result;
};

export const removeOthersData = <T extends ColumnChartData | IChart>(chartData: T[]): T[] =>
    chartData?.filter((item) => item.id !== OTHERS_DOMAIN_NAME);

export const filterGraphResponseBySelectedKeys = (data: SwitchGraphDomainData[], keys: Domain[]) => {
    const visibleDomainIds = new Set(keys.filter((domain) => domain.visible).map((domain) => domain.id));
    return data.filter((domainData) => visibleDomainIds.has(domainData.Id));
};

export const filterResponseBySelectedKeys = (data: Records, keys: Domain[]) => {
    const result: Records = [];

    if (keys?.length) {
        const keysMap = new Map();

        keys.forEach((key) => {
            keysMap.set(key.id, key);
        });

        data.filter((item) => {
            const isVisible = keysMap.get(item.Id)?.visible;

            if (isVisible) {
                result.push(item);
            }
        });
    }

    return { Data: result };
};

export const getIndustrieById = (id: string) => {
    const selectedCategory = categoryService.categoryQueryParamToCategoryObject(id);

    if (selectedCategory?.forApi?.includes("$")) {
        return categoryService.getCategory(selectedCategory?.forApi, "forApi");
    }

    return UserCustomCategoryService.getCustomAndSharedCategoryById(selectedCategory?.forApi);
};

export const getApiParams = (
    apiParams: ApiParams,
    duration: { from: string; to: string; isWindow?: boolean; latest?: string },
    filters: FixmeType,
    marketSharesMetricType: MarketSharesMetricType = MARKET_SHARES_GRAPH_METRIC_TYPE,
) => {
    const { from, to, latest, isWindow } = duration;
    const { webSource, country } = apiParams;

    const categoryParam = filters.category ?? apiParams.category;
    const primaryCategory = extractPrimaryCategoryFromParams(categoryParam) ?? "";
    const industry = getIndustrieById(primaryCategory);

    const newApiParams = {
        timeGranularity: resolveMarketSharesApiTimeGranularity(filters, isWindow, marketSharesMetricType),
        webSource,
        isWindow,
        country,
        from,
        to,
        latest,
        includeSubDomains: true,
        mtd: false,
        category: industry?.forApi ?? primaryCategory,
        keys: industry?.forApi ?? primaryCategory,
    };

    return newApiParams;
};

export const getIsMarketSharesHidden = () => {
    const isMarketSharesAllowed = !get(swSettings, "components.MarketPlayers.isAllowed", false);

    return isMarketSharesAllowed;
};

export const createLegendsItems = (series: (ColumnChartData & { name: string; segmentId: string })[]) => {
    const legendItems: Array<{
        index: number;
        color: string;
        domain: string;
        segmentName: string | undefined;
        smallIcon: boolean;
        id: string;
        type: string;
    }> = [];

    series.forEach(({ color, id, name, domain, isSegment }, index) => {
        legendItems.push({
            index,
            color,
            domain: domain || id,
            segmentName: isSegment ? name : undefined,
            smallIcon: true,
            id,
            type: "Website",
        });
    });

    return legendItems;
};
